<template>
    <!-- Currency Blocks -->
    <div class="currency-blocks-wrapper" :key="selectedTab + selectedCurrency">
        <!-- Block for only one currency -->
        <currency-table-block
        v-for="currency in allCurrencies"
        :key="currency.currency"
        :currency="currency"
        :savedBackState="savedBackState"
        :accountFinancialType="selectedTab"
        :isMobile="getMobileAppState"
        @mobileRowClick="mobileRowClickHandler"/>
    </div>
    <!-- Mobile details modal -->
    <product-details-modal
    v-if="getMobileAppState && showMobileDetails"
    :headerColor="detailsParams.headerColor"
    :title="detailsParams.title"
    :subTitle="detailsParams.subTitle"
    :detailsData="detailsParams.accounts"
    @hideMe="showMobileDetails = false" />
</template>

<script setup>
import CurrencyTableBlock from '@/components/features/accounts/patrimony/table/CurrencyTableBlock'
import ProductDetailsModal from '@/components/features/accounts/patrimony/table/mobile/ProductDetailsModal'

/* Vue && Assets */
import { ref, computed, onMounted } from 'vue'
import { calculateAccountPercentage } from '@/assets/scripts/parsers/patrimony'
import { amountFormat, currencyParser } from '@/assets/scripts/amounts'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* Emmitters */
const emit = defineEmits(['updateSelectedTab'])

/** props received
@selectedCurrency {string} - selected currency in our picker
@savedBackState {Object} - saved back state
@selectedTab {String, Number}
*/
const props = defineProps({
    selectedCurrency: String,
    savedBackState: Object,
    selectedTab: [String, Number]
})

const { getCurrenciesList, getAccountsProductsList, getPatrimonyAccountsList } = mapGetters(moduleMapper.ACCOUNTS_PATRIMONY)
const { getMobileAppState, getAppThemeMode } = mapGetters()

const allCurrencies = computed(() => {
    let totalCurrencies = []
    const currenciesOnly = getCurrenciesList.value.filter(obj => obj.currency !== 'allCurrencies')

    currenciesOnly.forEach(currency => {
        const totalProducts = []
        getAccountsProductsList.value(null, getAppThemeMode.value).forEach(product => {
            if (getPatrimonyAccountsList.value(currency.name, product.id, props.selectedTab).length) totalProducts.push(product)
        })

        if (totalProducts.length) totalCurrencies.push(currency)
    })

    if (props.selectedCurrency !== 'allCurrencies') totalCurrencies = totalCurrencies.filter(obj => obj.name === props.selectedCurrency)

    return totalCurrencies
})

/**********************************************************************
*                                                                     *
*                           Mobile Details                            *
*                                                                     *
**********************************************************************/
const showMobileDetails = ref(false)
const detailsParams = ref([])

const mobileRowClickHandler = (row) => {
    showMobileDetails.value = true
    detailsParams.value = {
        title: row.description,
        subTitle: amountFormat(Math.abs(row.totalAmount), currencyParser(row.currency)),
        headerColor: row.headerColor,
        accounts: row.accounts.map(account => ({
            ...account,
            percentage: `${calculateAccountPercentage(account.balance, row.totalAmount)}%`
        }))
    }
}

onMounted(() => {
    if (props.savedBackState && props.savedBackState.tabSelected) emit('updateSelectedTab', props.savedBackState.tabSelected)
})
</script>

<style lang="scss" scoped>
.generic-tabs-wrapper {
    width: auto;
    margin: 12px 0 16px 0;
}

.currency-blocks-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    gap: 8px;
}
</style>
